import React from "react";
import { TrendingUp, Building2, Shield, Heart, Star, CheckCircle2, ArrowRight } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

const ProductHubSection = () => {
  const products = [
    {
      id: "mutual-funds",
      icon: TrendingUp,
      title: "Mutual Funds",
      subtitle: "Wealth Creation Engine",
      description:
        "Professional fund management for long-term wealth creation. Beat inflation and build corpus for your dreams through diversified portfolios managed by experts.",
      color: "emerald",
      highlights: [
        "Professionally managed portfolios",
        "Start with as low as ₹500/month",
        "Inflation-beating returns (12-15% CAGR)",
        "Tax benefits under 80C (ELSS)",
      ],
      idealFor: "Long-term goals: Retirement, Child Education, Wealth Building",
      badge: "Most Popular",
    },
    {
      id: "corporate-fd",
      icon: Building2,
      title: "Corporate Fixed Deposits",
      subtitle: "Stable Returns, Higher Yield",
      description:
        "Earn 1-2% higher interest than traditional bank FDs with the safety of AAA and AA+ rated corporate deposits. Perfect for conservative investors seeking steady growth.",
      color: "blue",
      highlights: [
        "AAA & AA+ rated companies only",
        "1-2% higher than bank FDs",
        "Fixed tenure: 1-5 years",
        "Quarterly/Monthly interest payout",
      ],
      idealFor: "Risk-averse investors, Senior citizens, Emergency fund parking",
      badge: "Safe & Stable",
    },
    {
      id: "life-insurance",
      icon: Shield,
      title: "Term Life Insurance",
      subtitle: "Family's Financial Foundation",
      description:
        "The most affordable way to secure your family's future. Ensure their lifestyle stays intact, loans get paid, and dreams continue – no matter what happens to you.",
      color: "amber",
      highlights: [
        "₹1 Crore cover at ₹500/month",
        "Covers outstanding loans",
        "Income replacement for family",
        "Tax benefits under 80C & 10(10D)",
      ],
      idealFor: "Primary earners, Home loan holders, Young families",
      badge: "Essential",
    },
    {
      id: "health-insurance",
      icon: Heart,
      title: "Health Insurance",
      subtitle: "Wealth Protection Shield",
      description:
        "Rising medical costs can wipe out years of savings overnight. A health policy ensures your wealth stays intact while you get the best medical care.",
      color: "rose",
      highlights: [
        "Cashless treatment at 10,000+ hospitals",
        "Cover for pre & post hospitalization",
        "No-claim bonus up to 100%",
        "Critical illness & daycare coverage",
      ],
      idealFor: "Everyone! Especially families with dependents",
      badge: "Don't Skip",
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: {
        bg: "bg-emerald-50",
        iconBg: "bg-emerald-100",
        iconText: "text-emerald-600",
        badge: "bg-emerald-500 text-white",
        border: "border-emerald-200 hover:border-emerald-400",
        check: "text-emerald-500",
      },
      blue: {
        bg: "bg-blue-50",
        iconBg: "bg-blue-100",
        iconText: "text-blue-600",
        badge: "bg-blue-500 text-white",
        border: "border-blue-200 hover:border-blue-400",
        check: "text-blue-500",
      },
      amber: {
        bg: "bg-amber-50",
        iconBg: "bg-amber-100",
        iconText: "text-amber-600",
        badge: "bg-amber-500 text-white",
        border: "border-amber-200 hover:border-amber-400",
        check: "text-amber-500",
      },
      rose: {
        bg: "bg-rose-50",
        iconBg: "bg-rose-100",
        iconText: "text-rose-600",
        badge: "bg-rose-500 text-white",
        border: "border-rose-200 hover:border-rose-400",
        check: "text-rose-500",
      },
    };
    return colors[color] || colors.emerald;
  };

  return (
    <section
      id="products"
      className="py-24 sm:py-32 bg-white"
      data-testid="product-hub-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-emerald-600 font-semibold mb-4">
            <Star className="w-4 h-4" />
            The Wealth Suite
          </span>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold leading-snug text-emerald-950 mb-4">
            Your Complete Financial Arsenal
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Four pillars that work together to <strong>create</strong>, <strong>protect</strong>, 
            and <strong>preserve</strong> your wealth. Each serves a unique purpose in your financial journey.
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {products.map((product) => {
            const colors = getColorClasses(product.color);
            return (
              <div
                key={product.id}
                className={`relative group rounded-3xl border-2 ${colors.border} bg-white p-6 md:p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1`}
                data-testid={`product-card-${product.id}`}
              >
                {/* Badge */}
                <Badge className={`absolute -top-3 right-6 ${colors.badge} px-3 py-1`}>
                  {product.badge}
                </Badge>

                {/* Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className={`w-14 h-14 ${colors.iconBg} rounded-2xl flex items-center justify-center`}>
                    <product.icon className={`w-7 h-7 ${colors.iconText}`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-emerald-950">{product.title}</h3>
                    <p className="text-sm text-slate-500">{product.subtitle}</p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-slate-600 leading-relaxed mb-6">{product.description}</p>

                {/* Highlights */}
                <div className="space-y-2 mb-6">
                  {product.highlights.map((highlight, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle2 className={`w-4 h-4 ${colors.check} flex-shrink-0`} />
                      <span className="text-sm text-slate-700">{highlight}</span>
                    </div>
                  ))}
                </div>

                {/* Ideal For */}
                <div className={`${colors.bg} rounded-xl p-4`}>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Ideal For</p>
                  <p className="text-sm font-medium text-slate-800">{product.idealFor}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-slate-600 mb-4">Not sure which product fits your needs?</p>
          <Button
            asChild
            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-emerald-500/25"
            data-testid="product-hub-cta"
          >
            <a href="#contact">
              Get Personalized Advice
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProductHubSection;
